/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class InfiniteSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.slider');
    this.grid = this.querySelector('.slider__grid');
    this.prevBtn = this.querySelector('[name="prev"]');
    this.nextBtn = this.querySelector('[name="next"]');

    this.originalSlides = Array.from(this.grid.children).filter(el => !el.classList.contains('slider__item--cloned')); // Ensure only original slides
    this.originalCount = this.originalSlides.length;

    // --- NEW: Read slides per page from data attribute ---
    this.slidesPerPage = parseInt(this.dataset.slidesPerPage, 10) || 1;
    if (this.slidesPerPage === 0) this.slidesPerPage = 1; // Ensure it's never zero

    // Autoplay setup
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 4000;
    this.autoplayInterval = null;

    // Flag to prevent infinite repositioning during button-triggered scrolls
    this.isProgrammaticScroll = false;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);

    if (typeof window.initLazyScript === 'function') {
      window.initLazyScript(this, this.init.bind(this));
    } else {
      this.init();
    }
  }

  init() {
    if (!this.slider || !this.grid || this.originalCount === 0) {
      console.warn('Slider elements not found or no slides to initialize.');
      return;
    }

    this.setupClones();
    this.addEventListeners();
    this.updatePagination();

    // Crucially, set initial scroll position to the beginning of the original slides
    this.slider.scrollLeft = this.startCloneCount * this.slideSpan;

    if (this.autoplayEnabled) {
      this.startAutoplay();
    }

    console.log('Infinite Slider Initialized:');
    console.log('  Original Slides Count:', this.originalCount);
    console.log('  Total Slides (with clones):', this.slides.length);
    console.log('  Slide Span:', this.slideSpan);
    console.log('  Slides Per Page (from data attribute):', this.slidesPerPage); // Log the source
    console.log('  Start Clone Count:', this.startCloneCount);
    console.log('  End Clone Count:', this.endCloneCount);
    console.log('  Initial ScrollLeft:', this.slider.scrollLeft);
  }

  setupClones() {
    // Clear any existing clones to prevent duplicates on resize/re-init
    this.grid.querySelectorAll('.slider__item--start-clone, .slider__item--end-clone').forEach(clone => clone.remove());

    // Calculate slideSpan and slidesPerPage BEFORE cloning for accurate measurements
    if (this.originalCount > 0) {
      const firstOriginalSlide = this.originalSlides[0];
      const secondOriginalSlide = this.originalSlides[1];

      if (secondOriginalSlide) {
        // More accurate if items have consistent margin/gap via CSS
        this.slideSpan = secondOriginalSlide.offsetLeft - firstOriginalSlide.offsetLeft;
      } else {
        // Fallback for single slide or no gap
        const computedStyle = getComputedStyle(firstOriginalSlide);
        const marginRight = parseFloat(computedStyle.marginRight) || 0;
        const gridComputedStyle = getComputedStyle(this.grid);
        const gridGap = parseFloat(gridComputedStyle.gap) || 0;
        this.slideSpan = firstOriginalSlide.offsetWidth + Math.max(marginRight, gridGap);
      }
    } else {
      this.slideSpan = 0; // No slides, no span
    }

    // Clone the entire set of slides for both directions
    this.startCloneCount = this.originalCount;
    this.endCloneCount = this.originalCount;

    // Create and append clones
    const startClones = this.originalSlides.map(slide => {
      const clone = slide.cloneNode(true);
      clone.classList.add('slider__item--cloned', 'slider__item--start-clone');
      return clone;
    });

    const endClones = this.originalSlides.map(slide => {
      const clone = slide.cloneNode(true);
      clone.classList.add('slider__item--cloned', 'slider__item--end-clone');
      return clone;
    });

    // Add clones to the DOM
    startClones.forEach(clone => this.grid.prepend(clone));
    endClones.forEach(clone => this.grid.append(clone));

    this.slides = Array.from(this.grid.children); // Update this.slides to include clones

    console.log("Calculated slide span:", this.slideSpan);
    console.log("Start clones:", startClones.length);
    console.log("End clones:", endClones.length);
  }

  addEventListeners() {
    // Remove previous listeners to prevent duplicates on re-init
    this.slider.removeEventListener('scroll', this.handleScroll);
    this.prevBtn?.removeEventListener('click', this.prev);
    this.nextBtn?.removeEventListener('click', this.next);
    window.removeEventListener('resize', this.handleResize);

    // Add current listeners
    this.slider.addEventListener('scroll', this.handleScroll, { passive: true });
    this.prevBtn?.addEventListener('click', this.prev);
    this.nextBtn?.addEventListener('click', this.next);
    window.addEventListener('resize', this.handleResize);

    // Stop autoplay on user interaction
    this.slider.addEventListener('pointerdown', this.stopAutoplay.bind(this));
    this.slider.addEventListener('wheel', this.stopAutoplay.bind(this));
    this.prevBtn?.addEventListener('click', this.stopAutoplay.bind(this));
    this.nextBtn?.addEventListener('click', this.stopAutoplay.bind(this));
  }

  handleScroll() {
    const currentScrollLeft = this.slider.scrollLeft;
    
    // Only handle infinite repositioning if this isn't a programmatic scroll from buttons
    if (!this.isProgrammaticScroll) {
      this.handleInfiniteReposition(currentScrollLeft);
    }
    
    this.updatePagination();
  }

  handleInfiniteReposition(currentScrollLeft) {
    const originalContentWidth = this.originalCount * this.slideSpan;
    const originalContentStart = this.startCloneCount * this.slideSpan;
    const originalContentEnd = originalContentStart + originalContentWidth;

    // Small epsilon for floating point precision
    const epsilon = 1;

    // Check if we've scrolled into the end clones (forward direction)
    if (currentScrollLeft >= originalContentEnd - epsilon) {
      const offset = currentScrollLeft - originalContentEnd;
      this.slider.style.scrollBehavior = 'auto';
      this.slider.scrollLeft = originalContentStart + offset;
      this.slider.offsetHeight; // Force repaint
      this.slider.style.scrollBehavior = '';
    }
    // Check if we've scrolled into the start clones (backward direction)  
    else if (currentScrollLeft <= originalContentStart + epsilon) {
      const offset = currentScrollLeft - originalContentStart;
      this.slider.style.scrollBehavior = 'auto';
      this.slider.scrollLeft = originalContentEnd + offset;
      this.slider.offsetHeight; // Force repaint
      this.slider.style.scrollBehavior = '';
    }
  }

  next() {
    this.isProgrammaticScroll = true;
    
    const currentScrollLeft = this.slider.scrollLeft;
    const targetScrollLeft = currentScrollLeft + (this.slidesPerPage * this.slideSpan);
    
    // Perform the scroll
    this.slider.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });

    // Reset the flag after the scroll animation completes
    setTimeout(() => {
      this.isProgrammaticScroll = false;
    }, 500);
  }
  

  prev() {
    this.isProgrammaticScroll = true;
    
    const targetScrollLeft = this.slider.scrollLeft - (this.slidesPerPage * this.slideSpan);

    this.slider.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });

    // Reset the flag after the scroll animation completes
    setTimeout(() => {
      this.isProgrammaticScroll = false;
    }, 500); // Give enough time for smooth scroll to complete
  }

  updatePagination() {
    const currentScrollLeft = this.slider.scrollLeft;
    const relativeScrollLeft = currentScrollLeft - (this.startCloneCount * this.slideSpan);
    const activeIndex = Math.round(Math.max(0, relativeScrollLeft) / this.slideSpan);

    // If you have pagination dots, update their active state here
    // e.g., this.querySelectorAll('.pagination__dot').forEach((dot, i) => {
    //   if (i === activeIndex % this.originalCount) dot.classList.add('is-active');
    //   else dot.classList.remove('is-active');
    // });
  }

  handleResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.stopAutoplay();
      this.init(); // Re-initialize to recalculate positions/clones if layout changed
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    }, 250);
  }

  startAutoplay() {
    if (this.autoplayInterval) {
      this.stopAutoplay();
    }
    this.autoplayInterval = setInterval(() => {
      this.next();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
}

customElements.define('infinite-slider', InfiniteSlider);